import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "./api";
import { Lock, ThumbsUp, ThumbsDown, Building2, User, Shield, Calendar, MessageSquare, Bot } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";

const HARDCODED_PASSWORD = "mySecret123";

const FeedbackPage = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accessGranted, setAccessGranted] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  useEffect(() => {
    if (!accessGranted) return;

    const fetchFeedback = async () => {
      try {
        const response = await api.get("/getfeedback");
        setFeedbackList(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to fetch feedback.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [accessGranted]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === HARDCODED_PASSWORD) {
      setAccessGranted(true);
    } else {
      alert("Incorrect password.");
    }
  };

  if (!accessGranted) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-gradient-to-b from-background to-muted/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Feedback Dashboard</CardTitle>
            <CardDescription>Enter password to view feedback history</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter password"
                className="h-12"
              />
              <Button type="submit" className="w-full h-12">
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="flex-none px-6 py-4 border-b bg-background/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Feedback Dashboard</h1>
              <p className="text-sm text-muted-foreground">{feedbackList.length} feedback entries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 px-4 py-6">
        <div className="max-w-6xl mx-auto space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          
          {error && (
            <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
              {error}
            </div>
          )}
          
          {!loading && feedbackList.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No feedback submitted yet.</p>
            </div>
          )}

          {feedbackList.map((fb) => (
            <Card key={fb.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedFeedback(expandedFeedback === fb.id ? null : fb.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Rating and basic info */}
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0",
                      fb.rating === 5 ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30"
                    )}>
                      {fb.rating === 5 ? (
                        <ThumbsUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                      ) : (
                        <ThumbsDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium line-clamp-2">{fb.feedback_text}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                        {fb.is_main_chat ? (
                          <Badge variant="secondary" className="text-xs">Main Chat</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Conv #{fb.predicted_conversation_number}</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Company info badges */}
                  <div className="flex flex-wrap gap-2 justify-end">
                    {fb.company_name && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {fb.company_name}
                      </Badge>
                    )}
                    {fb.is_admin && (
                      <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        <Shield className="h-3 w-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded content */}
              {expandedFeedback === fb.id && fb.selected_messages?.length > 0 && (
                <div className="border-t bg-muted/30 p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Bot className="h-4 w-4" />
                    Selected Messages
                  </h4>
                  <div className="space-y-3">
                    {fb.selected_messages.map((msg, index) => (
                      <div 
                        key={index}
                        className={cn(
                          "p-3 rounded-lg",
                          msg.sender === 'bot' ? "bg-card border" : "bg-primary/10"
                        )}
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          {msg.sender === 'bot' ? (
                            <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          <span className="text-xs font-medium text-muted-foreground uppercase">
                            {msg.sender === 'bot' ? 'AI Response' : 'User'}
                          </span>
                        </div>
                        <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown>{String(msg.message || '')}</ReactMarkdown>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Additional metadata */}
                  <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                    <div>
                      <span className="text-muted-foreground">User ID</span>
                      <p className="font-medium">{fb.user_id}</p>
                    </div>
                    {fb.company_id && (
                      <div>
                        <span className="text-muted-foreground">Company ID</span>
                        <p className="font-medium">
                          {fb.company_id}
                          {fb.company_name && (
                            <span className="ml-2 text-muted-foreground">({fb.company_name})</span>
                          )}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Feedback ID</span>
                      <p className="font-medium">#{fb.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Rating</span>
                      <p className="font-medium">{fb.rating === 5 ? 'Positive' : 'Negative'}</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default FeedbackPage;
